export function cn(...inputs: Array<string | false | null | undefined>) {
  return inputs.filter(Boolean).join(" ");
}

/**
 * Detect if running in a Capacitor app wrapper
 * This is useful for optimizing image loading and other behaviors
 */
export function isCapacitorApp(): boolean {
  if (typeof window === "undefined") return false;
  
  /* Ask Capacitor what platform it is on. The old last clause here was
     `window.Capacitor !== undefined`, which is NOT evidence of running in the app: the
     shim is loaded on the plain website too because native integrations import
     @capacitor/core into the shared client bundle. Measured in Chromium
     against the dev server: window.Capacitor defined, getPlatform() "web",
     isNativePlatform() false. So this answered true in a desktop browser, and the landing
     page consequently applied its Android scroll workaround to every desktop Chrome
     visitor. lib/keyboard.ts already had it right; this is the same test. */
  const cap = (window as unknown as {
    Capacitor?: { getPlatform?: () => string; isNativePlatform?: () => boolean };
  }).Capacitor;
  if (typeof cap?.isNativePlatform === "function" && cap.isNativePlatform()) return true;
  if (cap?.getPlatform?.() && cap.getPlatform!() !== "web") return true;

  return (
    /Capacitor/i.test(navigator.userAgent) ||
    window.location.protocol === "capacitor:" ||
    window.location.origin.includes("capacitor://")
  );
}

/**
 * The value for the X-Platform header on auth requests, or null when this is not a native shell.
 *
 * The server uses this to decide whether to require a Turnstile token, because the bot check cannot
 * complete reliably inside either WebView. On Android the User-Agent also carries "Capacitor"
 * (capacitor.config.ts appends it), but on iOS it does not, so for iPhones this header is the only
 * signal the server gets. Both call sites in lib/auth/auth.ts use this rather than repeating the
 * platform logic, because when they drifted apart iPhone users were locked out of the app entirely.
 */
export function nativePlatformHeader(): "android-capacitor" | "ios-capacitor" | null {
  if (typeof window === "undefined") return null;
  if (!isCapacitorApp()) return null;
  if (isAndroid()) return "android-capacitor";
  if (isIOS()) return "ios-capacitor";
  return null;
}

/**
 * Detect if running on Android platform
 * Used for Android-specific fixes (e.g., removing safe-area padding when using status bar overlay)
 */
export function isAndroid(): boolean {
  if (typeof window === "undefined") return false;
  
  // Check Capacitor platform first (most reliable)
  const Capacitor = (window as any).Capacitor;
  if (Capacitor?.getPlatform) {
    return Capacitor.getPlatform() === "android";
  }
  
  // Fallback to user agent detection
  return /Android/i.test(navigator.userAgent) || 
         document.documentElement.classList.contains("platform-android");
}

/**
 * Detect if running in Chrome or Chromium-based browser (desktop or mobile)
 * Used for Chrome-specific scroll fixes - Chrome handles touch-action differently than Safari
 */
export function isChrome(): boolean {
  if (typeof window === "undefined") return false;
  const ua = navigator.userAgent;
  return /Chrome|Chromium|CriOS/i.test(ua) && !/Edge|Edg|OPR|SamsungBrowser/i.test(ua);
}

/**
 * Detect if running on iOS (iPhone/iPad) - includes Capacitor iOS and Safari
 * Used for iOS-specific fixes (e.g., scroll container, touch handling)
 */
export function isIOS(): boolean {
  if (typeof window === "undefined") return false;
  
  // Check Capacitor platform first (most reliable for native app)
  const Capacitor = (window as any).Capacitor;
  if (Capacitor?.getPlatform) {
    return Capacitor.getPlatform() === "ios";
  }
  
  // Fallback to user agent / class detection
  return /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
         document.documentElement.classList.contains("platform-ios");
}

// Re-export desktop detection utilities
export { isDesktop, isIOS as isIOSDevice, isAndroidDevice } from "./utils/desktop-detection";
